import { useState } from 'react';

import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { PasswordField } from '@components/password-field';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';

import { api } from 'api';
import { CountryCode } from 'components/CountryCode';
import { useAuthenticatedUser } from 'features/authentication';
import { Modal } from 'features/modals';
import { useStore } from 'models/Provider';

export const AddSMSAuthenticator = observer(({ enable, closeModal }) => {
  const { t, userStore } = useStore();
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [countryCode, setCountryCode] = useState('IS +354');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const me = useAuthenticatedUser();

  async function checkPassword() {
    setLoading(true);
    const res = await api.authentication.checkPassword(me.email, password);
    if (res.error) {
      setPasswordError(t('modals.addsmsauthenticator.password.error'));
    } else {
      setStep(2);
    }
    setLoading(false);
  }

  async function onSetPhoneNumber() {
    if (phone === '') {
      setPhoneError(t('modals.addsmsauthenticator.phonenumber.required'));
    } else {
      setLoading(true);
      const res = await userStore.addSMSNumber(password, countryCode.substr(3) + phone);
      if (res.error) {
        setPhoneError(t('modals.addsmsauthenticator.phonenumber.error'));
      } else {
        setStep(3);
      }
      setLoading(false);
    }
  }

  async function onSetCode() {
    setLoading(true);
    const res = await userStore.addSMSAuthenticator(password, code);
    if (res.error) {
      setCodeError(res.data.device);
    } else {
      enable(countryCode.substr(3) + phone);
      setStep(4);
    }
    setLoading(false);
  }
  return (
    <Modal>
      {step === 1 && (
        <>
          <Modal.Header text={t('modals.addsmsauthenticator.header')} />
          <Stack space={4}>
            <Text text={t('modals.addsmsauthenticator.subheader')} />
            <PasswordField
              label={t('modals.addsmsauthenticator.password.label')}
              value={password}
              onChange={setPassword}
              error={
                passwordError &&
                passwordError.split('\n').map((item, i) => <div key={i}>{item}</div>)
              }
            />
          </Stack>
          <Modal.Actions>
            <Button
              isDisabled={loading}
              variant="secondary"
              onClick={closeModal}
              text={t('modals.addsmsauthenticator.button.cancel')}
            />
            <Button
              isDisabled={loading}
              onClick={checkPassword}
              text={t('modals.addsmsauthenticator.button.submit')}
            />
          </Modal.Actions>
        </>
      )}
      {step === 2 && (
        <>
          <Modal.Header text={t('modals.addsmsauthenticator.header')} />
          <Stack space={4}>
            <Text text={t('modals.addsmsauthenticator.step2.helptext')} />
          </Stack>
          <Stack space={4} orientation="horizontal">
            {/* Will be replaced with phone number field */}
            <div style={{ width: '160px' }}>
              <CountryCode
                onChange={setCountryCode}
                defaultValue={countryCode}
                label={t('modals.addsmsauthenticator.countrycode.label')}
              />
            </div>
            <TextField
              label={t('modals.addsmsauthenticator.phonenumber.label')}
              value={phone}
              onChange={setPhone}
              error={
                phoneError && phoneError.split('\n').map((item, i) => <div key={i}>{item}</div>)
              }
            />
          </Stack>

          <Modal.Actions>
            <Button
              isDisabled={loading}
              variant="secondary"
              onClick={closeModal}
              text={t('modals.addsmsauthenticator.button.cancel')}
            />
            <Button
              isDisabled={loading}
              onClick={onSetPhoneNumber}
              text={t('modals.addsmsauthenticator.button.submit')}
            />
          </Modal.Actions>
        </>
      )}
      {step === 3 && (
        <>
          <Modal.Header text={t('modals.addsmsauthenticator.header')} />
          <Stack space={4}>
            <Text
              text={t('modals.addsmsauthenticator.step3.helptext', countryCode.substr(3) + phone)}
            />
            <TextField
              label={t('modals.addsmsauthenticator.label.code')}
              value={code}
              onChange={setCode}
              error={codeError && codeError.split('\n').map((item, i) => <div key={i}>{item}</div>)}
            />
          </Stack>

          <Modal.Actions>
            <Button
              isDisabled={loading}
              variant="secondary"
              onClick={() => {
                setCode('');
                setStep(2);
              }}
              text={t('modals.addsmsauthenticator.button.back')}
            />
            <Button
              isDisabled={loading}
              onClick={onSetCode}
              text={t('modals.addsmsauthenticator.button.submit')}
            />
          </Modal.Actions>
        </>
      )}
      {step === 4 && (
        <>
          <Modal.Header text={t('modals.addsmsauthenticator.header.success')} />
          <Text text={t('modals.addsmsauthenticator.subheader.success')} />

          <Modal.Actions>
            <Button onClick={closeModal} text={t('modals.addsmsauthenticator.button.submit')} />
          </Modal.Actions>
        </>
      )}
    </Modal>
  );
});
